/* eslint-disable no-magic-numbers */
import { AlertColor, Theme } from '@mui/material';
import { DoorBatteryWarningLevel } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { Colors } from 'theme/theme';
import { Icons } from 'ui/atoms/Icon/Icon';

export type BatteryStatus =
  | 'BatteryDefault'
  | 'BatteryLow'
  | 'BatteryCritical'
  | 'BatteryEmpty'
  | 'BatteryPowered'
  | 'BatteryUnknown';

const useBatteryStatusUI = (translation: Translation) => {
  interface BatteryStatusUI {
    iconColor: keyof Colors;
    iconName: Icons;
    drawerIconColor: string;
    textColor: string;
    iconOpacity: null | string;
    CellText: string;
    firstLineText: string;
    secondLineText: null | string;
    sortNumber: number;
    severity: AlertColor;
    boxText: string;
    backgroundColor: string;
  }

  const getBatteryData = (status: DoorBatteryWarningLevel, theme: Theme): BatteryStatusUI => {
    switch (status) {
      case DoorBatteryWarningLevel.BatteryLow:
        return {
          iconColor: 'warning',
          iconName: 'BatteryLow',
          drawerIconColor: theme.palette.warning.main,
          iconOpacity: null,
          textColor: '#FFE2B7',
          CellText: translation.warning,
          firstLineText: translation.warning,
          secondLineText: translation.action_required_replace_battery,
          sortNumber: 3,
          severity: 'warning',
          boxText: translation.battery_low,
          backgroundColor: '#ED6C0214'
        };
      case DoorBatteryWarningLevel.BatteryCritical:
        return {
          iconColor: 'warning1',
          iconName: 'BatteryCritical',
          drawerIconColor: theme.palette.error.main,
          iconOpacity: null,
          textColor: '#FFE2B7',
          CellText: translation.critical,
          firstLineText: translation.critical_lock_can_only_be_opened_with_calamity_card,
          secondLineText: translation.action_required_replace_battery,
          sortNumber: 2,
          severity: 'error',
          boxText: translation.battery_critical,
          backgroundColor: '#ED6C0214'
        };
      case DoorBatteryWarningLevel.BatteryEmpty:
        return {
          iconColor: 'error',
          iconName: 'BatteryEmpty',
          drawerIconColor: theme.palette.error.main,
          iconOpacity: null,
          textColor: '#F4C7C7',
          CellText: translation.empty,
          firstLineText: translation.empty_lock_is_not_working_anymore,
          secondLineText: translation.action_required_replace_battery,
          sortNumber: 1,
          severity: 'error',
          boxText: translation.battery_empty,
          backgroundColor: '#ED6C0214'
        };
      case DoorBatteryWarningLevel.Powered:
        return {
          iconColor: 'primary',
          iconName: 'BatteryPowered',
          drawerIconColor: theme.palette.success.main,
          iconOpacity: '0.25',
          textColor: '#CCE8CD',
          CellText: '',
          firstLineText: translation.lock_is_powered,
          secondLineText: null,
          sortNumber: 5,
          severity: 'success',
          boxText: translation.powered,
          backgroundColor: '#2E7D3214'
        };
      case DoorBatteryWarningLevel.BatteryDefault:
        return {
          iconColor: 'primary',
          iconName: 'BatteryDefault',
          drawerIconColor: theme.palette.success.main,
          iconOpacity: '0.25',
          textColor: '#CCE8CD',
          CellText: '',
          firstLineText: translation.battery_level_is_normal,
          secondLineText: null,
          sortNumber: 4,
          severity: 'success',
          boxText: '',
          backgroundColor: '#2E7D3214'
        };
      default:
      case DoorBatteryWarningLevel.BatteryUnknown:
        return {
          iconColor: 'primary',
          iconName: 'BatteryUnknown',
          drawerIconColor: theme.palette.success.main,
          iconOpacity: null,
          textColor: '#CCE8CD',
          CellText: 'Unknown',
          firstLineText: translation.battery_state_unknown,
          secondLineText: null,
          sortNumber: 0,
          severity: 'success',
          boxText: translation.battery_state_unknown,
          backgroundColor: '#ED6C0214'
        };
    }
  };

  return { getBatteryData };
};

export default useBatteryStatusUI;
